import * as React from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { PauseIcon } from "@phosphor-icons/react/dist/ssr/Pause";
import { PlayIcon } from "@phosphor-icons/react/dist/ssr/Play";
import { SpeakerHighIcon } from "@phosphor-icons/react/dist/ssr/SpeakerHigh";

import { useWorkspaceVoices, type PlatformSetting } from "@/hooks/api/use-platform-settings";
import { useAudioPreview } from "@/hooks/use-audio-preview";

import { parseVoiceIds, VOICE_OPTIONS_KEY } from "./lib";

export interface VoicesSectionProps {
	item: PlatformSetting;
	draft: string | undefined;
	disabled: boolean;
	onChange: (key: string, value: string) => void;
}

// Admin curation of the workspace TTS voices: checked voices are stored as
// VOICE_OPTIONS (JSON array of ids) and become the wizard's voice choices.
export function VoicesSection({ item, draft, disabled, onChange }: VoicesSectionProps): React.JSX.Element {
	const voicesQuery = useWorkspaceVoices();
	const voices = voicesQuery.data ?? [];

	// One shared audio element for previews.
	const { playingId, toggle: togglePreview } = useAudioPreview();

	const selectedIds = parseVoiceIds(draft ?? item.value);
	// Curated ids whose voice no longer exists in the workspace plugin — kept
	// visible so they can be unchecked (the backend rejects unknown ids on save).
	const unknownIds = selectedIds.filter((id) => !voices.some((voice) => voice.id === id));

	const toggleVoice = (voiceId: string) => {
		const next = new Set(selectedIds);
		if (next.has(voiceId)) {
			next.delete(voiceId);
		} else {
			next.add(voiceId);
		}
		// Deterministic order: workspace order first, then leftover unknown ids.
		const ordered = [
			...voices.filter((voice) => next.has(voice.id)).map((voice) => voice.id),
			...selectedIds.filter((id) => next.has(id) && !voices.some((voice) => voice.id === id)),
		];
		onChange(VOICE_OPTIONS_KEY, JSON.stringify(ordered));
	};

	if (voicesQuery.isPending) {
		return (
			<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
				{[0, 1, 2, 3].map((index) => (
					<Skeleton height={72} key={index} variant="rounded" />
				))}
			</Box>
		);
	}

	if (voicesQuery.isError) {
		return (
			<Alert
				action={
					<Button color="inherit" onClick={() => voicesQuery.refetch()} size="small">
						Retry
					</Button>
				}
				severity="warning"
			>
				We couldn&apos;t load the workspace voices right now — please try again in a moment.
			</Alert>
		);
	}

	// Curated ids outlive the workspace: the list can be empty while VOICE_OPTIONS still holds
	// ids (an env-provided curation, or a workspace that is not provisioned yet). Returning the
	// empty state here would hide them - and claim the opposite of what onboarding will do.
	if ((voicesQuery.data === null || voices.length === 0) && unknownIds.length === 0) {
		return (
			<Card sx={{ p: 3, textAlign: "center" }} variant="outlined">
				<Stack spacing={1} sx={{ alignItems: "center" }}>
					<Avatar sx={{ bgcolor: "var(--mui-palette-background-level2)", color: "var(--mui-palette-text-secondary)" }}>
						<SpeakerHighIcon fontSize="var(--Icon-fontSize)" />
					</Avatar>
					<Typography variant="subtitle1">No workspace voices yet</Typography>
					<Typography color="text.secondary" variant="body2" sx={{ maxWidth: 440 }}>
						Configure an ElevenLabs connection in the AX Platform workspace to pick voices. Until then, new customers
						see the built-in default voices.
					</Typography>
				</Stack>
			</Card>
		);
	}

	return (
		<Stack spacing={2}>
			<Typography color="text.secondary" variant="body2">
				Check the voices you want to offer. Changes apply after you save.
			</Typography>
			<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
				{voices.map((voice) => {
					const selected = selectedIds.includes(voice.id);
					const playing = playingId === voice.id;

					return (
						<Card
							key={voice.id}
							sx={{
								borderColor: selected ? "var(--mui-palette-primary-main)" : "var(--mui-palette-divider)",
								borderWidth: selected ? 2 : 1,
							}}
							variant="outlined"
						>
							<Stack direction="row" sx={{ alignItems: "stretch" }}>
								<CardActionArea
									disabled={disabled}
									onClick={() => toggleVoice(voice.id)}
									sx={{ alignItems: "center", display: "flex", justifyContent: "flex-start", p: 1, pr: 0 }}
								>
									<Checkbox checked={selected} disabled={disabled} sx={{ pointerEvents: "none" }} tabIndex={-1} />
									<Typography sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }} variant="subtitle2">
										{voice.name}
									</Typography>
								</CardActionArea>
								<Box sx={{ alignItems: "center", display: "flex", pr: 1 }}>
									<Tooltip title={voice.previewUrl ? "Play preview" : "No preview available"}>
										<span>
											<IconButton
												disabled={!voice.previewUrl}
												onClick={() => {
													if (voice.previewUrl) {
														togglePreview(voice.id, voice.previewUrl);
													}
												}}
											>
												{playing ? <PauseIcon size={20} weight="fill" /> : <PlayIcon size={20} weight="fill" />}
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							</Stack>
						</Card>
					);
				})}
				{unknownIds.map((id) => (
					<Card key={id} sx={{ borderStyle: "dashed" }} variant="outlined">
						<Stack direction="row" sx={{ alignItems: "center", p: 1 }}>
							<Checkbox checked disabled={disabled} onChange={() => toggleVoice(id)} />
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="subtitle2">Unknown voice</Typography>
								<Typography color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }} variant="caption">
									{id} — no longer in the workspace; uncheck to remove.
								</Typography>
							</Box>
						</Stack>
					</Card>
				))}
			</Box>
		</Stack>
	);
}
